// ---------------------------------------------------------------------------
// THE OPERATION VOCABULARY.
//
// This is the entire contract between the language model and Mantle. The model
// never writes SQL, never returns rows, and never sees a number: it turns a
// sentence into ONE of the objects below, and something else executes it.
//
//     "take last year's opex and add 5%"
//         -> { op: 'escalate', method: 'pct', value: 5, basis: 'version:V1' }
//
// WHY IT IS SHAPED THIS WAY, because each property was bought deliberately:
//
//   * The model cannot invent a value. It emits a percentage; the arithmetic is
//     JavaScript you can unit-test. A hallucinated cell is not expressible.
//   * The model cannot widen its own blast radius. Scope is a filter over
//     declared dimensions, validated before anything runs.
//   * No customer data goes in the prompt. "Increase OPEX by 5%" needs the
//     instruction and the vocabulary, not a single budget figure — so a client's
//     numbers never leave the building, and prompt injection has no payload to
//     carry because the model is not reading their data.
//   * Swapping Claude for Gemini is a swap. The contract is this file.
//
// TWO EXECUTORS, ONE VOCABULARY:
//
//   applyToRows()  — client-side, mutates what is on screen, saved by the page's
//                    own Save button. No new write path exists, so the existing
//                    validation and role checks cannot be bypassed.
//   server-side    — for anything larger than the loaded page. See needsServer().
// ---------------------------------------------------------------------------

// Everything the model is allowed to ask for. Anything outside this list is a
// refusal, not an improvisation.
export const OPERATIONS = {
  escalate: {
    label: 'Escalate',
    summary: 'Move every matching value by a percentage or a fixed amount',
    clientSide: true,
  },
  set: {
    label: 'Set',
    summary: 'Set every matching value to the same number',
    clientSide: true,
  },
  spread: {
    label: 'Spread',
    summary: 'Distribute an annual total evenly across the periods',
    clientSide: true,
  },
  copy_from_version: {
    label: 'Copy from version',
    summary: 'Take the values from another budget version as the starting point',
    // The other version's data is not on screen, so this can never be a
    // client-side transform however small the page is.
    clientSide: false,
  },
  apply_index: {
    label: 'Apply index',
    summary: 'Escalate against a published price index rather than a flat rate',
    clientSide: false,
  },
};

// ---------------------------------------------------------------- describe ---
//
// The operation in a sentence, for the confirmation panel. THIS IS A SAFETY
// CONTROL, not a nicety: it is where a user catches "fifty" heard as "fifteen",
// so it states the number, the direction and the scope explicitly rather than
// paraphrasing them away.
export function describe(op, ctx = {}) {
  if (!op) return '';
  const scope = describeScope(op, ctx);
  const field = op.field_label || 'every value';

  switch (op.op) {
    case 'escalate': {
      const dir = op.value < 0 ? 'Decrease' : 'Increase';
      const amount = op.method === 'pct'
        ? `${Math.abs(op.value)}%`
        : `${Math.abs(op.value).toLocaleString()} per line`;
      const basis = op.basis ? ` based on ${op.basis_label || op.basis}` : '';
      return `${dir} ${field} by ${amount}${basis} — ${scope}`;
    }
    case 'set':
      // Always two decimals. "42,5" in a ZA locale is one glance away from
      // reading as forty-two thousand five, and this panel is the last place a
      // wrong number can be caught.
      return `Set ${field} to ${Number(op.value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} — ${scope}`;
    case 'spread':
      return `Spread ${Number(op.value).toLocaleString()} evenly across all periods of ${field} — ${scope}`;
    case 'copy_from_version':
      return `Copy ${field} from ${op.basis_label || op.basis} — ${scope}`;
    case 'apply_index':
      return `Escalate ${field} against the ${op.index_label || op.index_code} index — ${scope}`;
    default:
      return 'Unrecognised operation';
  }
}

function describeScope(op, ctx) {
  const bits = [];
  bits.push(ctx.versionLabel || 'the selected version');
  bits.push(ctx.siteLabel || 'all sites');
  if (op.filter?.text) bits.push(`lines matching "${op.filter.text}"`);
  else bits.push('every line on this page');
  return bits.join(' · ');
}

// ------------------------------------------------------------------ scope ---
//
// THE MOST IMPORTANT FUNCTION IN THIS FILE.
//
// Mantle's grids hold a PAGE, not a dataset — gridQuery returns 100 rows by
// default. So "increase all OPEX by 5%" applied to what is on screen changes a
// hundred lines, leaves the other eleven hundred untouched, and looks like it
// worked. The user saves, sees green, and most of the budget is unchanged.
//
// That is the worst failure this feature can have, because it is invisible. So
// scope is checked before anything is applied, and when the request is bigger
// than the page the UI SAYS SO and hands off, rather than quietly doing the
// visible rows.
export function needsServer(op, { loadedCount, totalCount, hasMore }) {
  if (!op) return false;
  if (!OPERATIONS[op.op]?.clientSide) return true;
  if (hasMore) return true;
  if (totalCount != null && totalCount > loadedCount) return true;
  return false;
}

export function scopeMessage({ loadedCount, totalCount, hasMore }) {
  if (totalCount != null && totalCount > loadedCount) {
    return `This page has ${loadedCount.toLocaleString()} of ${totalCount.toLocaleString()} matching lines loaded.`;
  }
  if (hasMore) {
    return `This page has ${loadedCount.toLocaleString()} lines loaded and there are more behind the filter.`;
  }
  return `All ${loadedCount.toLocaleString()} matching lines are loaded.`;
}

// ------------------------------------------------------------------ apply ---
//
// Runs an operation against the rows already on screen and returns the CHANGES
// rather than the new rows — the host page owns its own state and its own dirty
// tracking, and gets to decide what a change means to it.
//
// `fields` are supplied by the host: { key, label, get(row), set(row, value),
// periods? }. The assistant knows nothing about period_data, qty_per or
// unit_price; every page teaches it how to read and write its own numbers,
// which is what lets one component serve OPEX, BOM, rates and prices.
export function applyToRows(op, rows, fields, { matcher } = {}) {
  const field = fields.find((f) => f.key === op.field) || fields[0];
  if (!field) return { changes: [], error: 'This page has nothing this operation can change.' };

  const match = matcher || defaultMatcher(op);
  const changes = [];

  rows.forEach((row) => {
    if (row.is_calculated) return;      // a calculated line is an output, never an input
    if (!match(row)) return;

    const slots = field.periods ? field.periods(row) : [null];
    slots.forEach((slot) => {
      const from = Number(field.get(row, slot)) || 0;
      let to = from;
      if (op.op === 'escalate') {
        to = op.method === 'pct' ? from * (1 + op.value / 100) : from + op.value;
      } else if (op.op === 'set') {
        to = op.value;
      } else if (op.op === 'spread') {
        to = op.value / (slots.length || 1);
      }
      to = Math.round(to * 100) / 100;
      if (to !== from) changes.push({ row, slot, field: field.key, from, to });
    });
  });

  return { changes, field };
}

// Scope inside the page: a text filter across the dimensions the host declared.
// Absent a filter, every row on the page matches — which is why needsServer()
// runs first.
function defaultMatcher(op) {
  const text = (op.filter?.text || '').trim().toLowerCase();
  if (!text) return () => true;
  return (row) => JSON.stringify(row).toLowerCase().includes(text);
}

// A one-line summary of what a set of changes does, for the banner and the
// audit record. Net movement matters more than row count: forty rows moving a
// thousand rand is a different decision from one row moving a million.
export function summarise(changes) {
  const net = changes.reduce((a, c) => a + (c.to - c.from), 0);
  const rows = new Set(changes.map((c) => c.row)).size;
  return { cellCount: changes.length, rowCount: rows, net };
}
